import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { env } from "../utils/env";
import { organisationGroup } from "../types/organiation";

export type OrganisationGraphDirection = "outbound" | "inbound" | "both";

/**
 * Filters that change *which* graph is requested, so changing one refetches.
 *
 * The client-side filters (profile type, profile status, minimum ownership
 * percentage) are applied to the graph already fetched and deliberately are not
 * part of this: they should not cost a request.
 */
export type OrganisationGraphFilters = {
  /** null or undefined means every level, and omits the parameter entirely */
  depth?: number | null;
  direction?: OrganisationGraphDirection;
  /** ProfileRelationshipType codes, sent comma separated */
  relationshipTypes?: string[];
  activeOnly?: boolean;
  includeRecords?: boolean;
};

const DEFAULT_FILTERS: OrganisationGraphFilters = {
  depth: null,
  direction: "both",
};

const buildParams = (filters: OrganisationGraphFilters) => {
  const params: Record<string, string | number | boolean> = {
    direction: filters.direction ?? "both",
  };

  /*
   * "All" is expressed by leaving depth out of the request rather than sending
   * a sentinel. The backend parses depth inside a bare `except ValueError`, so
   * an unparseable value is swallowed and silently treated as unlimited, which
   * is how the previous literal "None" happened to work.
   */
  if (typeof filters.depth === "number") {
    params.depth = filters.depth;
  }

  if (filters.relationshipTypes?.length) {
    params.relationship_types = filters.relationshipTypes.join(",");
  }

  if (typeof filters.activeOnly === "boolean") {
    params.active_only = filters.activeOnly;
  }

  if (filters.includeRecords) {
    params.include_records = true;
  }

  return params;
};

const fetchOrganisationGraph =
  (authHeader: string, id: number, filters: OrganisationGraphFilters) =>
  async (): Promise<organisationGroup> => {
    const res = await axios.get<organisationGroup>(
      `${env.verifyServiceUrl}individual/profile/${id}/organisation-graph/`,
      {
        headers: { Authorization: authHeader },
        params: buildParams(filters),
      },
    );
    return res.data;
  };

export const useOrganisationData = (
  id: number,
  filters: OrganisationGraphFilters = DEFAULT_FILTERS,
) => {
  const { authHeader } = useAuth();

  // the resolved params go in the key, so any filter change refetches
  return useQuery(
    ["orgData", id, buildParams(filters)],
    fetchOrganisationGraph(authHeader, id, filters),
    {
      enabled: Number.isInteger(id),
      retry: false,
    },
  );
};
